import * as React from "react";
import { CheckCircle2, CircleAlert, InfoIcon, TriangleAlert } from "lucide-react";

import { getErrorMessage } from "@/common/utils/error";
import { cn } from "@/common/utils/className";
import { ExpandableHelp } from "@/domain/ui/ExpandableHelp";
import { NOTIFICATION_LEVEL, type NotificationLevel } from "@/domain/ui/notificationLevels";

const NOTIFICATION_ROLE = {
  [NOTIFICATION_LEVEL.INFO]: "status",
  [NOTIFICATION_LEVEL.SUCCESS]: "status",
  [NOTIFICATION_LEVEL.WARNING]: "status",
  [NOTIFICATION_LEVEL.ERROR]: "alert",
} as const;

const NOTIFICATION_CONFIG = {
  [NOTIFICATION_LEVEL.INFO]: {
    icon: InfoIcon,
    className: "border-primary/20 bg-primary/5",
    accentClassName: "bg-primary",
    iconClassName: "border-primary/20 bg-primary/10 text-primary",
  },
  [NOTIFICATION_LEVEL.SUCCESS]: {
    icon: CheckCircle2,
    className: "border-emerald-500/30 bg-emerald-500/10",
    accentClassName: "bg-emerald-500",
    iconClassName: "border-emerald-500/30 bg-emerald-500/10 text-emerald-500",
  },
  [NOTIFICATION_LEVEL.WARNING]: {
    icon: TriangleAlert,
    className: "border-yellow-500/30 bg-yellow-500/10",
    accentClassName: "bg-yellow-500",
    iconClassName: "border-yellow-500/30 bg-yellow-500/10 text-yellow-500",
  },
  [NOTIFICATION_LEVEL.ERROR]: {
    icon: CircleAlert,
    className: "border-destructive/30 bg-destructive/10",
    accentClassName: "bg-destructive",
    iconClassName: "border-destructive/30 bg-destructive/10 text-destructive",
  },
} as const;

interface NotificationProps extends Omit<React.ComponentProps<"div">, "title"> {
  level?: NotificationLevel;
  title: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
}

function Notification({
  level = NOTIFICATION_LEVEL.INFO,
  title,
  icon,
  actions,
  className,
  children,
  role,
  ...props
}: NotificationProps) {
  const config = NOTIFICATION_CONFIG[level];
  const Icon = config.icon;

  return (
    <div
      data-slot="notification"
      role={role ?? NOTIFICATION_ROLE[level]}
      className={cn("relative overflow-hidden rounded-lg border px-4 py-4 text-sm", config.className, className)}
      {...props}
    >
      <div
        aria-hidden
        className={cn("absolute inset-y-4 left-0 w-1 rounded-r-full opacity-80", config.accentClassName)}
      />
      <div className="flex items-start gap-3 pl-3">
        <div className={cn("flex size-9 shrink-0 items-center justify-center rounded-full border", config.iconClassName)}>
          {icon ?? <Icon className="size-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-foreground font-medium">{title}</p>
          {children && <div className="text-muted-foreground mt-1 space-y-3 [&_strong]:text-foreground">{children}</div>}
          {actions && <div className="mt-3 flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      </div>
    </div>
  );
}

interface ErrorNotificationProps extends Omit<NotificationProps, "level" | "icon" | "children"> {
  error?: unknown;
  message?: React.ReactNode;
}

function ErrorNotification({ error, message, actions, ...props }: ErrorNotificationProps) {
  const details = error === undefined || error === null ? null : getErrorMessage(error);

  return (
    <Notification level={NOTIFICATION_LEVEL.ERROR} actions={actions} {...props}>
      {message && <p>{message}</p>}
      {details && (
        <ExpandableHelp label="Show details" expandedLabel="Hide details">
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-background/60 p-3 font-mono text-xs text-muted-foreground">
            {details}
          </pre>
        </ExpandableHelp>
      )}
    </Notification>
  );
}

export { Notification, ErrorNotification };
